/**
 * Links (admin only):
 *  - Login: POST {id}/link-user and {id}/unlink-user (Member.Write). One login per live member.
 *  - Planning Centre: search candidates, link and unlink (api/members/{id}/planning-centre/*;
 *    Admin, Member.Write or Member.Update).
 *  - Anatomy (Acts API): status, check-and-remember (POST anatomy people/check with the member id)
 *    and clear (Anatomy.Manage).
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  IconAlertCircle, IconLink, IconUnlink, IconUserCircle, IconSearch, IconPlugConnected, IconHeartbeat, IconExternalLink, IconCheck,
} from '@tabler/icons-react';
import { Card, Stack, Group, Button, Text, Alert, Badge, Select, TextInput, Avatar, SimpleGrid, ThemeIcon, Skeleton } from '@/components/ui';
import { toast } from '@/components/ui/providers/AlertProvider';
import { modal } from '@/components/ui/providers/ModalsProvider';
import { usePermissions } from '@/components/ccms-components/auth/hooks/usePermissions';
import { useAnatomyService, useMemberService, useUserService, type PlanningCentrePersonCandidate, type UserModelView } from '@actschurch/ccms-sdk';
import type { MemberTabProps } from '../types';
import { useUserLookup } from '../UserName';
import { useInvalidateMember } from '../useMemberMutations';
import { errorMessage, formatStampTime, initialsOf, memberFullName } from '../memberFormat';

function useDebounced<T>(value: T, ms = 350): T {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

function userLabel(u: UserModelView): string {
  const name = u.displayName || [u.firstName, u.surname].filter(Boolean).join(' ') || u.userName || 'Unnamed user';
  return u.email ? `${name} (${u.email})` : name;
}

export function LinksTab({ api, member, perms }: MemberTabProps) {
  const navigate = useNavigate();
  const { hasRole } = usePermissions();
  const memberService = useMemberService();
  const userService = useUserService();
  const anatomyService = useAnatomyService();
  const invalidate = useInvalidateMember(api);
  const name = memberFullName(member) || `Member ${member.memberID}`;
  const isDeleted = !!member.isDeleted;

  // ---------- Login ----------
  const linkedUser = useUserLookup(member.userId || null);
  const [userSearch, setUserSearch] = useState('');
  const userTerm = useDebounced(userSearch.trim());
  const [pickedUserId, setPickedUserId] = useState<string | null>(null);
  const users = useQuery({
    queryKey: ['users', 'search', userTerm],
    queryFn: () => userService.search({ searchTerm: userTerm, page: 1, pageSize: 20 }),
    enabled: !member.userId && perms.linkUser && userTerm.length >= 2,
    staleTime: 30_000,
  });
  const userOptions = useMemo(
    () => (users.data ?? []).filter((u) => !!u.id).map((u) => ({ value: u.id as string, label: userLabel(u) })),
    [users.data],
  );

  const linkUser = useMutation({
    mutationFn: (userId: string) => memberService.linkUser(member.memberID, userId),
    onSuccess: () => { toast.success('Login linked', `${name} can now sign in with this login.`); setPickedUserId(null); setUserSearch(''); invalidate('all'); },
    onError: (e) => toast.error('Could not link the login', errorMessage(e)),
  });
  const unlinkUser = useMutation({
    mutationFn: () => memberService.unlinkUser(member.memberID),
    onSuccess: () => { toast.success('Login unlinked'); invalidate('all'); },
    onError: (e) => toast.error('Could not unlink the login', errorMessage(e)),
  });

  // ---------- Planning Centre ----------
  const [pcQuery, setPcQuery] = useState(name);
  const [pcSearch, setPcSearch] = useState<string | null>(null);
  const candidates = useQuery({
    queryKey: [...api.scope, 'pc-candidates', pcSearch],
    queryFn: () => memberService.getPlanningCentreCandidates(member.memberID, pcSearch ?? ''),
    enabled: perms.planningCentre && pcSearch !== null && !member.planningCentrePersonId,
    retry: false,
  });
  const linkPc = useMutation({
    mutationFn: (c: PlanningCentrePersonCandidate) => memberService.linkPlanningCentre(member.memberID, c.planningCentrePersonId),
    onSuccess: () => { toast.success('Linked to Planning Centre'); setPcSearch(null); invalidate(); },
    onError: (e) => toast.error('Could not link to Planning Centre', errorMessage(e)),
  });
  const unlinkPc = useMutation({
    mutationFn: () => memberService.unlinkPlanningCentre(member.memberID),
    onSuccess: () => { toast.success('Planning Centre link removed'); invalidate(); },
    onError: (e) => toast.error('Could not remove the Planning Centre link', errorMessage(e)),
  });

  // ---------- Anatomy ----------
  const checkAnatomy = useMutation({
    mutationFn: () => anatomyService.checkPerson({
      name: member.firstName ?? '',
      surname: member.lastName ?? '',
      email: member.email || null,
      mobile: member.cellNumber || null,
      memberId: member.memberID,
    }),
    onSuccess: (r) => {
      if (r.found) toast.success('Found in Anatomy', `Anatomy user ${r.anatomyUserId} is now linked.`);
      else toast.info('Not found in Anatomy', r.message || 'No Anatomy user matches these details.');
      invalidate();
    },
    onError: (e) => toast.error('Could not check Anatomy', errorMessage(e)),
  });
  const clearAnatomy = useMutation({
    mutationFn: () => anatomyService.linkMember(member.memberID, { anatomyUserId: 0 }),
    onSuccess: () => { toast.success('Anatomy link cleared'); invalidate(); },
    onError: (e) => toast.error('Could not clear the Anatomy link', errorMessage(e)),
  });

  const u = linkedUser.data;

  return (
    <Stack gap="md">
      {isDeleted && <Alert color="yellow" icon={<IconAlertCircle size={16} />}>This record is deleted. Restore it on the Admin tab before changing links.</Alert>}

      {/* Login */}
      <Card withBorder padding="lg" radius="md">
        <Group gap="sm" mb="sm">
          <ThemeIcon variant="light" color="indigo" size="lg"><IconUserCircle size={18} /></ThemeIcon>
          <div>
            <Text fw={600}>Login</Text>
            <Text size="sm" c="dimmed">The CCMS login this member signs in with. Visitors and children usually have none.</Text>
          </div>
        </Group>
        {member.userId ? (
          <Group justify="space-between" wrap="wrap">
            {linkedUser.isLoading ? <Skeleton height={40} width={260} /> : (
              <Group gap="sm" wrap="nowrap">
                <Avatar src={u?.avatar || undefined} radius="xl">{initialsOf(u ? userLabel(u) : name)}</Avatar>
                <Stack gap={0}>
                  <Text size="sm" fw={600}>{u ? (u.displayName || [u.firstName, u.surname].filter(Boolean).join(' ') || u.userName) : 'Linked login'}</Text>
                  <Text size="xs" c="dimmed">{u?.email ?? (linkedUser.isError ? 'User details could not be loaded' : member.userId)}</Text>
                </Stack>
                <Badge color="green" variant="light" leftSection={<IconCheck size={10} />}>Linked</Badge>
              </Group>
            )}
            <Group gap="xs">
              {hasRole('Admin') && (
                <Button variant="light" size="xs" leftSection={<IconExternalLink size={14} />} onClick={() => navigate(`/admin/users/${member.userId}`)}>Open user</Button>
              )}
              {perms.linkUser && (
                <Button variant="light" color="red" size="xs" leftSection={<IconUnlink size={14} />} loading={unlinkUser.isPending} disabled={isDeleted}
                  onClick={() => modal.confirm({
                    title: 'Unlink this login?',
                    body: `${name} keeps the member record, but the login no longer opens it. Their self-service profile will be empty until a member is linked again.`,
                    confirmLabel: 'Unlink',
                    confirmColor: 'red',
                    onConfirm: () => unlinkUser.mutate(),
                  })}>
                  Unlink login
                </Button>
              )}
            </Group>
          </Group>
        ) : perms.linkUser ? (
          <Stack gap="sm">
            <Text size="sm">No login is linked. Find the person's login to link it.</Text>
            <Group align="flex-end" wrap="wrap">
              <Select style={{ flex: 1, minWidth: 260 }} label="Login" placeholder="Search by name or email" searchable clearable
                data={userOptions} value={pickedUserId} onChange={setPickedUserId} searchValue={userSearch} onSearchChange={setUserSearch}
                filter={({ options }) => options} disabled={isDeleted}
                nothingFoundMessage={userTerm.length < 2 ? 'Type at least two letters' : users.isFetching ? 'Searching' : 'No logins found'} />
              <Button leftSection={<IconLink size={16} />} disabled={!pickedUserId || isDeleted} loading={linkUser.isPending}
                onClick={() => pickedUserId && linkUser.mutate(pickedUserId)}>
                Link login
              </Button>
            </Group>
            {users.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(users.error)}</Alert>}
            {linkUser.isError && <Alert color="red" icon={<IconAlertCircle size={16} />} title="Not linked">{errorMessage(linkUser.error)}</Alert>}
          </Stack>
        ) : (
          <Text size="sm" c="dimmed">No login is linked.</Text>
        )}
      </Card>

      <SimpleGrid cols={{ base: 1, md: 2 }}>
        {/* Planning Centre */}
        <Card withBorder padding="lg" radius="md">
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="violet" size="lg"><IconPlugConnected size={18} /></ThemeIcon>
            <Text fw={600}>Planning Centre</Text>
          </Group>
          {member.planningCentrePersonId ? (
            <Stack gap="xs">
              <Group gap={6}><Badge color="violet" variant="light">Linked</Badge><Text size="sm">Person {member.planningCentrePersonId}</Text></Group>
              <Text size="xs" c="dimmed">
                {member.planningCentreLinkedAt ? `Since ${formatStampTime(member.planningCentreLinkedAt)}` : 'Link date not recorded'}
                {member.planningCentreLinkSource ? ` · ${member.planningCentreLinkSource}` : ''}
              </Text>
              {perms.planningCentre && (
                <Group><Button size="xs" variant="light" color="red" leftSection={<IconUnlink size={14} />} loading={unlinkPc.isPending} disabled={isDeleted}
                  onClick={() => modal.confirm({ title: 'Remove the Planning Centre link?', body: 'Check-ins and sync stop matching this person until they are linked again.', confirmLabel: 'Remove link', confirmColor: 'red', onConfirm: () => unlinkPc.mutate() })}>
                  Remove link
                </Button></Group>
              )}
            </Stack>
          ) : !perms.planningCentre ? (
            <Text size="sm" c="dimmed">Not linked. Linking Planning Centre people needs the Member.Write right.</Text>
          ) : (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">Not linked. Search Planning Centre for this person.</Text>
              <Group align="flex-end" wrap="nowrap">
                <TextInput style={{ flex: 1 }} label="Search" value={pcQuery} onChange={(e) => setPcQuery(e.currentTarget.value)} disabled={isDeleted}
                  onKeyDown={(e) => { if (e.key === 'Enter' && pcQuery.trim()) setPcSearch(pcQuery.trim()); }} />
                <Button variant="light" leftSection={<IconSearch size={16} />} disabled={!pcQuery.trim() || isDeleted} loading={candidates.isFetching}
                  onClick={() => setPcSearch(pcQuery.trim())}>Search</Button>
              </Group>
              {candidates.isError && <Alert color="red" icon={<IconAlertCircle size={16} />}>{errorMessage(candidates.error)}</Alert>}
              {pcSearch !== null && !candidates.isFetching && !candidates.isError && (candidates.data ?? []).length === 0 && (
                <Text size="sm" c="dimmed">No Planning Centre people match.</Text>
              )}
              {(candidates.data ?? []).map((c) => (
                <Group key={c.planningCentrePersonId} justify="space-between" wrap="nowrap">
                  <Group gap="sm" wrap="nowrap">
                    <Avatar src={c.avatarUrl || undefined} radius="xl" size="sm">{initialsOf(`${c.firstName ?? ''} ${c.lastName ?? ''}`)}</Avatar>
                    <Stack gap={0}>
                      <Text size="sm" fw={500}>{[c.firstName, c.lastName].filter(Boolean).join(' ') || `Person ${c.planningCentrePersonId}`}</Text>
                      <Text size="xs" c="dimmed">{c.email || `ID ${c.planningCentrePersonId}`}</Text>
                    </Stack>
                  </Group>
                  <Button size="xs" variant="light" leftSection={<IconLink size={14} />} loading={linkPc.isPending && linkPc.variables?.planningCentrePersonId === c.planningCentrePersonId}
                    onClick={() => linkPc.mutate(c)}>Link</Button>
                </Group>
              ))}
            </Stack>
          )}
        </Card>

        {/* Anatomy */}
        <Card withBorder padding="lg" radius="md">
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="red" size="lg"><IconHeartbeat size={18} /></ThemeIcon>
            <Text fw={600}>Anatomy</Text>
          </Group>
          {member.anatomyUserId ? (
            <Stack gap="xs">
              <Group gap={6}><Badge color="red" variant="light">Linked</Badge><Text size="sm">Anatomy user {member.anatomyUserId}</Text></Group>
              <Text size="xs" c="dimmed">
                {member.anatomyLinkedAt ? `Since ${formatStampTime(member.anatomyLinkedAt)}` : 'Link date not recorded'}
                {member.anatomyLinkSource ? ` · ${member.anatomyLinkSource}` : ''}
              </Text>
              {perms.anatomyManage && (
                <Group><Button size="xs" variant="light" color="red" leftSection={<IconUnlink size={14} />} loading={clearAnatomy.isPending} disabled={isDeleted}
                  onClick={() => modal.confirm({ title: 'Clear the Anatomy link?', body: 'The next Anatomy check looks this person up again.', confirmLabel: 'Clear link', confirmColor: 'red', onConfirm: () => clearAnatomy.mutate() })}>
                  Clear link
                </Button></Group>
              )}
            </Stack>
          ) : (
            <Stack gap="sm">
              <Text size="sm" c="dimmed">Not linked yet. The Anatomy desk links people when it finds them.</Text>
              {perms.anatomyView && (
                <Group>
                  <Button size="xs" variant="light" leftSection={<IconSearch size={14} />} loading={checkAnatomy.isPending}
                    disabled={isDeleted || !member.firstName || !member.lastName}
                    onClick={() => checkAnatomy.mutate()}>
                    Check Anatomy now
                  </Button>
                </Group>
              )}
              {perms.anatomyView && (!member.firstName || !member.lastName) && <Text size="xs" c="dimmed">A first name and surname are needed to check.</Text>}
              {checkAnatomy.data && !checkAnatomy.data.found && <Text size="sm">{checkAnatomy.data.message || 'No Anatomy user matches these details.'}</Text>}
            </Stack>
          )}
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
